<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import type { ActiveSwitchTest } from '@/types'
import { useSwitchStore } from '@/stores/switch'
import { formatTimer } from '@/utils/format'
import SwitchProgressBar from '@/components/switch/SwitchProgressBar.vue'

const props = defineProps<{ tests?: ActiveSwitchTest[] }>()
const switchStore = useSwitchStore()
const now = ref(Date.now())
let tick: ReturnType<typeof setInterval> | null = null

const tests = computed(() => props.tests ?? switchStore.activeTestList)

function secondsLeftOf(test: ActiveSwitchTest): number {
  return Math.max(0, Math.floor((test.expiresAtMs - now.value) / 1000))
}

function startTick() {
  stopTick()
  now.value = Date.now()
  tick = setInterval(() => {
    now.value = Date.now()
    if (tests.value.some((t) => secondsLeftOf(t) <= 0)) {
      void switchStore.poll()
    }
  }, 1000)
}

function stopTick() {
  if (tick) {
    clearInterval(tick)
    tick = null
  }
}

watch(
  () => tests.value.length,
  (n) => {
    if (n > 0) startTick()
    else stopTick()
  },
  { immediate: true },
)

onMounted(() => {
  if (tests.value.length) startTick()
})

onUnmounted(stopTick)

async function confirmSwitch(test: ActiveSwitchTest) {
  const res = await switchStore.confirmSwitch(test.switchId)
  if (!res.success) alert(res.message || 'Не удалось подтвердить смену')
}

async function cancelSwitch(test: ActiveSwitchTest) {
  if (!confirm('Отменить смену? Новый сервер будет заблокирован для вас.')) return
  const res = await switchStore.cancelSwitch(test.switchId)
  if (!res.success) alert(res.message || 'Не удалось отменить смену')
}
</script>

<template>
  <div
    v-for="(msg, gk) in switchStore.hungMessages"
    :key="'hung-' + gk"
    class="alert alert-danger mb-4"
  >
    {{ msg }}
  </div>

  <div
    v-for="test in tests"
    :key="test.switchId"
    class="panel mb-4 border border-primary/20 bg-primary-light/40"
  >
    <div class="mb-3 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h2 class="text-lg font-bold">Тестирование смены сервера</h2>
        <p class="mt-1 text-sm text-muted">
          Проверьте подключение. Осталось {{ formatTimer(secondsLeftOf(test)) }}.
        </p>
      </div>
      <div class="text-2xl font-extrabold text-primary">{{ formatTimer(secondsLeftOf(test)) }}</div>
    </div>

    <div class="mb-3">
      <SwitchProgressBar :progress="Math.max(0, Math.min(100, (secondsLeftOf(test) / 3600) * 100))" />
    </div>

    <div class="mb-4 grid gap-1 text-sm">
      <div><span class="text-muted">Старый:</span> {{ test.oldServerName }} ({{ test.oldServerLocation }})</div>
      <div><span class="text-muted">Новый:</span> {{ test.newServerName }} ({{ test.newServerLocation }})</div>
    </div>

    <div class="alert alert-warning mb-4 text-sm">
      При отмене новый сервер будет заблокирован для вас навсегда.
    </div>

    <div class="flex flex-wrap gap-2">
      <button
        type="button"
        class="btn btn-outline-danger"
        :disabled="!!switchStore.cancelling[test.switchId] || !!switchStore.confirming[test.switchId] || secondsLeftOf(test) <= 0"
        @click="cancelSwitch(test)"
      >
        {{ switchStore.cancelling[test.switchId] ? 'Отмена…' : 'Отменить смену' }}
      </button>
      <button
        type="button"
        class="btn btn-primary"
        :disabled="!!switchStore.confirming[test.switchId] || !!switchStore.cancelling[test.switchId] || secondsLeftOf(test) <= 0"
        @click="confirmSwitch(test)"
      >
        {{ switchStore.confirming[test.switchId] ? 'Подтверждение…' : 'Подтвердить смену' }}
      </button>
    </div>
  </div>
</template>
